import { apiRequest } from '@/utils/api-request';
import { CustomNode } from '@/types/node-types';
import { CustomEdge } from '@/types/edge-types';
import * as Sentry from '@sentry/react';

export interface ResumeWorkflowResponse {
  success: boolean;
  message: string;
  session_id: string;
  edited_node_count?: number;
  /**
   * True when the backend discovered the workflow was already
   * terminal on Temporal and reconciled the PG row.  Caller should
   * tear down the pause-panel UI rather than show an error.  See
   * the matching field on ``PauseWorkflowResponse`` for the full
   * rationale.
   */
  was_already_completed?: boolean;
  detail?: string;
}

/**
 * Optional payload for resume.
 *
 * * ``payload`` -- full AdvancedWorkflowPayload-shape dict.  Required
 *   when the user edited the canvas during the pause; absent for a
 *   plain resume.
 * * ``editedNodeIds`` -- react_flow_generated_ids whose semantic
 *   config or upstream connections changed.  The backend computes
 *   the downstream closure of this set and invalidates every
 *   matching node_result row so they recompute on resume.
 *
 * Sending ``payload`` without ``editedNodeIds`` is valid: the new
 * payload is swapped in but no cached results are invalidated
 * (typical when the user only rearranged nodes on the canvas).
 *
 * Sending ``editedNodeIds`` without ``payload`` is rejected by the
 * backend with a 400 -- we need the new payload to seed the
 * resumed run from.
 */
export interface ResumeWorkflowOptions {
  payload?: {
    id: string;
    session_id: string;
    name: string;
    nodes: CustomNode[];
    edges: CustomEdge[];
    [k: string]: unknown;
  };
  editedNodeIds?: string[];
}

/**
 * Resume a paused workflow.
 *
 * Identity rules of the resumed run:
 * - The session_id is unchanged from the original run -- this is the
 *   same WorkflowRun row, the same Temporal workflow execution, and
 *   the same WebSocket channel.  The user does not need to reconnect
 *   anything.
 * - When ``payload`` is sent, the backend creates a new RUN
 *   workflow_version and re-points the run's version_id at it.  The
 *   original version stays as the immutable audit record of what
 *   was originally executed.
 * - The downstream closure of ``editedNodeIds`` has its
 *   ``node_result`` rows deleted (CASCADE on files) so the resumed
 *   run recomputes them.  Already-completed nodes outside that
 *   closure keep their cached results and get skipped on resume.
 *
 * Lifecycle:
 * - Backend signals AgenticWorkflow.resume(...).  Workflow applies
 *   the edit (if any), then re-enters its dispatch loop.
 * - WebSocket emits ``lifecycle/edit_applied`` (if an edit was
 *   applied) followed by ``lifecycle/resumed``.  The status bar
 *   flips to RUNNING on ``resumed``.
 */
export const resumeWorkflow = async (
  workflowId: string,
  sessionId: string,
  orgId: string,
  options: ResumeWorkflowOptions = {},
  signal?: AbortSignal,
): Promise<ResumeWorkflowResponse> => {
  const body: Record<string, unknown> = {};
  if (options.payload !== undefined) {
    body.payload = options.payload;
  }
  if (options.editedNodeIds !== undefined) {
    body.edited_node_ids = options.editedNodeIds;
  }

  try {
    const response = await apiRequest<ResumeWorkflowResponse>({
      url: `/workflow/${orgId}/${workflowId}/${sessionId}/resume-workflow`,
      method: 'POST',
      target: 'workflow',
      body,
      signal,
    });
    return response as ResumeWorkflowResponse;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: 'resume-workflow',
        workflowId,
        sessionId,
        hasPayload: options.payload !== undefined,
        editedCount: options.editedNodeIds?.length ?? 0,
      },
    });
    throw error;
  }
};
